// ─── HubSpot Product (from API) ─────────────────────────────────────────────

export interface HubSpotProduct {
  id: string;
  name: string;
  price: number;
  category: string;
  subCategory?: string;
  sku: string;
  description?: string;
  shortDescription?: string;
  imageUrl?: string;
}

// ─── HubSpot Field (from API) ────────────────────────────────────────────────

export interface HubSpotFieldOption {
  label: string;
  value: string;
}

export interface HubSpotField {
  name: string;
  label: string;
  options: HubSpotFieldOption[];
}

// ─── Configurator Flow Model ────────────────────────────────────────────────

export interface ShowWhen {
  stepId: string;
  selectedAny: string[]; // product IDs
}

export interface ExclusionCondition {
  stepId: string;        // prior step to watch
  selectedAny: string[]; // if any of these are selected in that step...
  excludeItems: string[]; // ...exclude these item IDs from this step
}

export interface ProductStepItem {
  uid: string;
  type: 'product';
  productId: string;
}

export interface FieldValueStepItem {
  uid: string;
  type: 'field_value';
  field: string;
  value: string;
  label: string;
}

export type StepItem = ProductStepItem | FieldValueStepItem;

export type SelectionType = "single" | "multi";

export const TRANSLATION_LOCALES = ["en", "de", "fr", "pt"] as const;
export type TranslationLocale = (typeof TRANSLATION_LOCALES)[number];
export const TRANSLATABLE_FIELDS = ["title", "description", "breadcrumb", "proTip"] as const;
export type TranslatableField = (typeof TRANSLATABLE_FIELDS)[number];

export type StepTranslation = Partial<Record<TranslatableField, string>>;
export type StepTranslations = Partial<Record<TranslationLocale, StepTranslation>>;

export interface Step {
  id: string;
  title: string;
  description: string;
  breadcrumb: string;
  proTip: string;
  selectionType: SelectionType;
  required: boolean;
  conditions: ShowWhen[];
  exclusions: ExclusionCondition[];
  items: StepItem[];
  calculatePricing?: boolean;
  /** Hide every price in this step — item cards and the final summary alike. */
  hidePricing?: boolean;
  priority?: number;
  translations?: StepTranslations;
}

// ─── JSON Output (what the customer-facing configurator consumes) ───────────

export interface FlowConfigOutput {
  configuratorId: string;
  version: number;
  steps: FlowStepOutput[];
}

export interface FlowStepOutput {
  stepId: string;
  title: string;
  description: string;
  breadcrumb: string;
  proTip: string;
  selectionType: SelectionType;
  required: boolean;
  calculatePricing: boolean;
  hidePricing: boolean;
  priority: number;
  conditions?: ShowWhen[];
  exclusions?: ExclusionCondition[];
  items: FlowItemOutput[];
}

export interface FlowItemOutput {
  itemType: 'product' | 'field_value';
  label: string;
  images?: string[];
  // product-only
  hubspotProductId?: string;
  sku?: string;
  price?: number;
  subCategory?: string;
  shortDescription?: string;
  featuredProduct?: string[];
  // field_value-only
  field?: string;
  value?: string;
}

// ─── Item Images ──────────────────────────────────────────────────────────────

export interface ItemImage {
  id: string;
  url: string;
}

// ─── Drag & Drop ────────────────────────────────────────────────────────────

export type DragSource = "catalog" | "step-reorder";

export interface CatalogDragData {
  source: "catalog";
  productId: string;
}

export interface StepReorderDragData {
  source: "step-reorder";
  stepId: string;
}

export type DragData = CatalogDragData | StepReorderDragData;

// ─── Preview ────────────────────────────────────────────────────────────────

export type PreviewTab = "customer" | "json";

export type CustomerSelections = Record<string, string[]>;
